import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CreditCard, AlertTriangle, Search } from 'lucide-react';
import FinancingBook from './FinancingBook';
import { InventoryPage } from './InventoryChrome';
import { billingApi, type FinanceAgreementRow, type AgreementRollupLike, type BillingSkuRow } from '../../services/billingApi';
import { hardwareApi, type DeviceBundleRow, type DeviceModelRow } from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * THE FINANCE BOOK — every device finance, lease and rental, on its own page.
 *
 * Moved off `/inventory/stock` (2026-08-30) where it rendered the entire book
 * with no paging and no filter, on a screen that is about devices. The route is
 * `finance-book`, NOT `agreements`, deliberately: `/inventory/agreements`
 * already exists and means SIGNATURE ENVELOPES. Two nav entries a character
 * apart, meaning different things, is how an operator opens the wrong one.
 */

const PAGE_SIZE = 50;

type StatusFilter = 'active' | 'ending' | 'closed' | 'all';
type TypeFilter = '' | 'installment' | 'lease' | 'rental';

const STATUS_CHIPS: Array<{ id: StatusFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'ending', label: 'Ending' },
  { id: 'closed', label: 'Closed' },
  { id: 'all', label: 'All' },
];

const TYPE_CHIPS: Array<{ id: TypeFilter; label: string }> = [
  { id: '', label: 'All kinds' },
  { id: 'installment', label: 'Finance' },
  { id: 'lease', label: 'Lease' },
  { id: 'rental', label: 'Rent' },
];

const FinanceBookPage: React.FC = () => {
  const [rows, setRows] = useState<FinanceAgreementRow[]>([]);
  const [totals, setTotals] = useState<AgreementRollupLike | undefined>(undefined);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<StatusFilter>('active');
  const [financeType, setFinanceType] = useState<TypeFilter>('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  // Reference data for the payback join — only needed for pre-ledger rows that
  // carry no snapshotted cost, but cheap enough to always have.
  const [skus, setSkus] = useState<BillingSkuRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [bundles, setBundles] = useState<DeviceBundleRow[]>([]);

  // Debounce the search box: one request per pause, not one per keystroke.
  // `q` is matched server-side against an already-lowercased haystack, so we
  // send the raw input verbatim — no client-side lowercasing or re-filtering.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Request-sequence guard. Without it, an in-flight fetch that resolves
  // AFTER a newer one can overwrite fresh rows with stale ones — and worse,
  // a stale "Load more" (append=true) resolving after a fresh filter-driven
  // replace would splice OLD-filter rows onto the NEW list via
  // `setRows(prev => [...prev, ...])`, silently mixing two different queries
  // into one agreements list with nothing to indicate it. A plain
  // "last write wins" doesn't catch that append case, so every state write
  // below is gated on this call still being the latest one.
  const requestSeq = useRef(0);

  const load = useCallback(async (token?: string) => {
    const seq = ++requestSeq.current;
    const append = !!token;
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const res = await billingApi.listFinancing({
        status,
        ...(financeType ? { financeType } : {}),
        ...(q ? { q } : {}),
        limit: PAGE_SIZE,
        ...(token ? { nextToken: token } : {}),
      });
      if (seq !== requestSeq.current) return; // superseded — drop this response
      setRows((prev) => (append ? [...prev, ...res.agreements] : res.agreements));
      setTotals(res.totals);
      // "Is there more" is decided from nextToken ONLY — DynamoDB applies its
      // filter after reading Limit, so a page may legitimately come back
      // short without meaning the book is exhausted.
      setNextToken(res.nextToken ?? null);
      setGeneratedAt(res.generatedAt);
    } catch (e: any) {
      if (seq !== requestSeq.current) return; // superseded — don't surface a stale error
      setError(e?.message || 'Failed to load the finance book');
    } finally {
      // Always clear THIS call's own loading flag, even if superseded: it's
      // the only thing that will ever turn it off (a newer non-append call
      // never touches loadingMore, and vice versa), so skipping this would
      // strand a spinner forever.
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [status, financeType, q]);

  useEffect(() => { load(); }, [load]);

  // Reference data — best-effort, loaded once. Failures degrade the payback
  // join (rows just show no payback) rather than blanking the book.
  useEffect(() => {
    Promise.all([
      billingApi.listSkus().catch(() => ({ items: [] as BillingSkuRow[] })),
      hardwareApi.listModels().catch(() => ({ items: [] as DeviceModelRow[] })),
      hardwareApi.listBundles().catch(() => ({ items: [] as DeviceBundleRow[] })),
    ]).then(([s, m, b]) => {
      setSkus((s.items || []).filter((x) => x.type === 'device_finance'));
      setModels(m.items || []);
      setBundles(b.items || []);
    });
  }, []);

  const chip = (active: boolean) =>
    `rounded-full px-3 py-1.5 text-[11px] font-semibold min-h-[44px] ${
      active ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
             : 'bg-white text-stone-600 border border-stone-300'
    }`;

  const body = useMemo(() => {
    if (loading) {
      return (
        <RubyLoader variant="page" />
      );
    }
    if (error) {
      return (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {error}
        </div>
      );
    }
    return (
      <FinancingBook
        rows={rows}
        skus={skus}
        models={models}
        bundles={bundles}
        generatedAt={generatedAt}
        onChanged={() => load()}
        showTotals
        serverTotals={totals}
        nextToken={nextToken}
        loadingMore={loadingMore}
        onLoadMore={() => nextToken && load(nextToken)}
      />
    );
  }, [loading, error, rows, skus, models, bundles, generatedAt, totals, nextToken, loadingMore, load]);

  return (
    <InventoryPage
      icon={CreditCard}
      title="Finance Book"
      subtitle="Every device finance, lease and rental — what is owed, what has been paid, and what has paid for itself"
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tenant or offer…"
            className="w-full rounded-lg border border-stone-300 py-2 pl-9 pr-3 text-xs text-stone-900 min-h-[44px]"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_CHIPS.map((c) => (
            <button key={c.id} onClick={() => setStatus(c.id)} className={chip(status === c.id)}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_CHIPS.map((c) => (
            <button key={c.id || 'any'} onClick={() => setFinanceType(c.id)} className={chip(financeType === c.id)}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      {body}
    </InventoryPage>
  );
};

export default FinanceBookPage;
